import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import { SortDescriptor } from "@progress/kendo-data-query";
import { GridColumn } from "@progress/kendo-react-grid";
import {
  AdminContentWrapper,
  AdminHeader,
  Loader,
  TableGrid,
  TableLastItem,
} from "../../components";
import { format } from "date-fns";
import {
  clearAdminEigentuemerwechselData,
  getAdminEigentuemerwechsel,
} from "../../store/slices/eigentuemerwechsel";
import appConfig from "../../util/appConfig";
import { statuses } from "../AdminEigentuemerwechselById";

const AdminEigentuemerwechsel = () => {
  const dispatch = useAppDispatch();
  const { isAdminLoaderActive } = useAppSelector((state) => state.admin);
  const { data, itemsCount } = useAppSelector(
    (state) => state.eigentuemerwechsel.admin,
  );
  const [sort, setSort] = useState<Array<SortDescriptor>>([]);
  const [value, setValue] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [datesChanged, setDatesChanged] = useState({});
  const [status, setStatus] = useState<string[]>([]);

  const onDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (end || end === null) {
      setDatesChanged({ updated: true });
    }
  };

  const changeStatuses = (st: string[]) => {
    setStatus([...st]);
  };

  const changeHandler = (val: string) => {
    setValue(val);
  };

  const pageChangeHandler = (page: number) => {
    setPage(page);
  };

  const pageSizeHandler = (size: number) => {
    setPage(1);
    setPageSize(size);
  };

  const generateStatusOptions = (type?: string) => {
    switch (type) {
      case "mags": {
        return [
          { label: statuses.check, value: "check" },
          { label: statuses.accepted, value: "accepted" },
          { label: statuses.declined, value: "declined" },
          { label: statuses.inWork, value: "inWork" },
        ];
      }
      case "viersen": {
        return [
          { label: statuses.check, value: "check" },
          { label: statuses.accepted, value: "accepted" },
          { label: statuses.declined, value: "declined" },
        ];
      }
      default: {
        return [
          { label: statuses.check, value: "check" },
          { label: statuses.accepted, value: "accepted" },
          { label: statuses.declined, value: "declined" },
          { label: statuses.inWork, value: "inWork" },
        ];
      }
    }
  };

  const selectorOptions = [
    {
      placeholder: "Bitte Status wählen",
      isActive: ["mags", "viersen"].includes(appConfig.logo),
      defaultValue: undefined,
      selectOptions: generateStatusOptions(appConfig.logo),
      value: status.map((el) => {
        return {
          value: el,
          label: statuses[el],
        };
      }),
      onChange: changeStatuses,
    },
  ];

  useEffect(() => {
    const abortController = new window.AbortController();
    const signal = abortController.signal;

    dispatch(
      getAdminEigentuemerwechsel({
        signal,
        direction: sort[0]?.dir,
        page,
        pageSize,
        searchValue: value,
        sort: sort[0]?.field,
        startDate: startDate ? new Date(startDate).toUTCString() : startDate,
        endDate: endDate ? new Date(endDate).toUTCString() : endDate,
        status,
      }),
    );

    return () => {
      if (signal && abortController.abort) {
        abortController.abort();
      }
    };
  }, [sort, page, pageSize, value, datesChanged, status]);

  useEffect(() => {
    return () => {
      dispatch(clearAdminEigentuemerwechselData());
    };
  }, []);

  return (
    <div>
      <AdminHeader pageTitle={"Eigentümerwechsel"} hasBtnGroup={false} />
      <AdminContentWrapper>
        <TableGrid
          data={data.map((el) => {
            return {
              ...el,
              // name: `${el.username} ${
              //   el.userSurname ? ", " + el.userSurname : ""
              // }`,
              // status: statuses[el.status],
              previousOwnerFullName: `${el.previousOwnerName} ${el.previousOwnerSurname}`,
              newOwnerFullName: `${el.newOwnerName} ${el.newOwnerSurname}`,
              createdAt: format(new Date(el.createdAt), "dd.MM.yyyy HH:mm"),
              status: el?.status ? statuses[el.status] : "",
            };
          })}
          sort={sort}
          sortable={true}
          onSortChange={setSort}
          withExcelExport={true}
          items={[
            { field: "createdAt", title: "Datum" },
            { field: "previousOwnerFullName", title: "Vorheriger Besitzer" },
            { field: "newOwnerFullName", title: "Neuer Besitzer" },
            ...(["mags", "viersen"].includes(appConfig.logo)
              ? [{ field: "status", title: "Status" }]
              : []),
          ]}
          searchInputValue={value}
          onSearchInputValueChange={changeHandler}
          withPager={true}
          page={page}
          pageSize={pageSize}
          pageChangeHandler={pageChangeHandler}
          pageSizeHandler={pageSizeHandler}
          itemsCount={itemsCount}
          onDateChange={onDateChange}
          startDate={startDate}
          endDate={endDate}
          withDatePicker
          lastItem={<GridColumn cell={TableLastItem} width="50px" />}
          selectorOptions={selectorOptions.filter((el) => el.isActive)}
        />
      </AdminContentWrapper>
      {isAdminLoaderActive && <Loader />}
    </div>
  );
};

export default AdminEigentuemerwechsel;
